import { Controller } from "@hotwired/stimulus"
import { loadChart } from "../lib/load_chart"

// Months of one metric at a time; the chips switch the metric and the view
// without a request.
//
// All brands together: a line of the month totals, the running month dashed
// from the last closed one to its forecast. By brand: bars stacked by brand,
// the forecast topping the running month in grey, or for a rate (conversion)
// a line per brand plus a bold one for all of them.
export default class extends Controller {
  static targets = ["canvas", "chip", "split"]
  static classes = ["active", "inactive"]
  static values = {
    data: Object,
    metric: { type: String, default: "revenue" },
    byBrand: { type: Boolean, default: false },
    forecastLabel: String,
    totalLabel: String
  }

  async connect() {
    this.Chart = await loadChart()
    if (this.element.isConnected) this.render()
  }

  disconnect() {
    this.chart?.destroy()
  }

  show({ params: { metric } }) {
    this.metricValue = metric
  }

  split({ params: { byBrand } }) {
    this.byBrandValue = byBrand
  }

  metricValueChanged() {
    this.mark(this.chipTargets, (chip) => chip.dataset.brandMonthsChartMetricParam === this.metricValue)
    if (this.Chart) this.render()
  }

  byBrandValueChanged() {
    this.mark(this.splitTargets, (button) => (button.dataset.brandMonthsChartByBrandParam === "true") === this.byBrandValue)
    if (this.Chart) this.render()
  }

  mark(buttons, isActive) {
    buttons.forEach((button) => {
      const active = isActive(button)
      button.classList.remove(...(active ? this.inactiveClasses : this.activeClasses))
      button.classList.add(...(active ? this.activeClasses : this.inactiveClasses))
      button.setAttribute("aria-pressed", active)
    })
  }

  render() {
    this.chart?.destroy()
    const metric = this.dataValue.metrics[this.metricValue]
    if (!metric) return

    const rate = metric.kind === "rate"
    const bars = this.byBrandValue && !rate
    const format = this.formatter(metric.unit)
    const forecastLabel = this.forecastLabelValue
    const last = metric.total.length - 1
    let datasets
    if (!this.byBrandValue) datasets = this.totalDatasets(metric)
    else if (rate) datasets = this.rateDatasets(metric)
    else datasets = this.stackedDatasets(metric)

    this.chart = new this.Chart(this.canvasTarget, {
      type: bars ? "bar" : "line",
      data: { labels: this.dataValue.labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { position: "bottom", labels: { boxWidth: 10, boxHeight: 10, font: { size: 11 } } },
          tooltip: {
            // The forecast's dashed line starts at the last closed month, which the totals already show
            filter: (item) => item.raw !== null && item.raw !== 0 &&
              !(item.dataset.label === forecastLabel && item.dataIndex !== last),
            callbacks: {
              label: (item) => item.dataset.label === forecastLabel
                ? `${forecastLabel}: ${format(metric.forecast)}`
                : `${item.dataset.label}: ${format(item.raw)}`,
              footer: (items) => (bars && items.length
                ? `${this.totalLabelValue}: ${format(metric.total[items[0].dataIndex])}`
                : "")
            }
          }
        },
        scales: {
          x: { stacked: bars, grid: { display: false }, ticks: { maxRotation: 0, autoSkipPadding: 8 } },
          y: {
            stacked: bars,
            beginAtZero: true,
            ticks: { callback: (value) => this.compact(value) + (metric.unit === "%" ? "%" : "") }
          }
        }
      }
    })
  }

  // The running month's count so far would dip the line, so while it has a
  // forecast the line stops at the last closed month and the forecast goes on dashed.
  totalDatasets(metric) {
    const last = metric.total.length - 1
    const forecast = metric.forecast != null && last > 0
    const line = { tension: 0.3, borderWidth: 2, pointRadius: 3, pointHoverRadius: 5, borderColor: "#1992D4" }
    const datasets = [{
      ...line,
      label: this.totalLabelValue,
      data: forecast ? metric.total.map((value, index) => (index === last ? null : value)) : metric.total,
      backgroundColor: "rgba(25, 146, 212, 0.1)",
      fill: true
    }]
    if (forecast) {
      datasets.push({
        ...line,
        label: this.forecastLabelValue,
        data: metric.total.map((value, index) => {
          if (index === last) return metric.forecast
          return index === last - 1 ? value : null
        }),
        backgroundColor: "#1992D4",
        borderDash: [5, 5],
        pointStyle: "rectRot",
        fill: false
      })
    }
    return datasets
  }

  stackedDatasets(metric) {
    const datasets = this.dataValue.brands.map((brand, index) => ({
      label: brand.name,
      data: metric.series[index],
      backgroundColor: brand.color,
      stack: "months"
    }))
    if (metric.forecast != null) {
      const last = metric.total.length - 1
      datasets.push({
        label: this.forecastLabelValue,
        data: metric.total.map((_, index) => (index === last ? Math.max(metric.forecast - metric.total[last], 0) : null)),
        backgroundColor: "#D9E2EC",
        borderColor: "#9FB3C8",
        borderWidth: 1,
        stack: "months"
      })
    }
    return datasets
  }

  rateDatasets(metric) {
    const line = { tension: 0.3, spanGaps: true, pointRadius: 2, borderWidth: 1.5 }
    return [
      { ...line, label: this.totalLabelValue, data: metric.total, borderColor: "#243B53", backgroundColor: "#243B53", borderWidth: 3, pointRadius: 3 },
      ...this.dataValue.brands.map((brand, index) => ({
        ...line, label: brand.name, data: metric.series[index], borderColor: brand.color, backgroundColor: brand.color
      }))
    ]
  }

  formatter(unit) {
    const number = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: unit === "%" ? 1 : 0 })
    if (unit === "%") return (value) => `${number.format(value)}%`
    if (unit) return (value) => `${number.format(value)} ${unit}`
    return (value) => number.format(value)
  }

  compact(value) {
    return new Intl.NumberFormat("ru-RU", { notation: "compact", maximumFractionDigits: 1 }).format(value)
  }
}
